import {
  mysqlTable,
  int,
  bigint,
  tinyint,
  varchar,
  text,
  mediumtext,
  longtext,
  double,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";
import { customFields, countries } from "./core";
import { currencies, products } from "./finance";
import { projects } from "./projects";

/**
 * Batch 1.8 — CRM & Support (reskin-docs/03 RBAC scopes; reskin-docs/07 money=Admin/HR).
 * Ported by reconciling each table's Schema::create + every up() ALTER across all 291
 * migrations (IGNORING down()). Anchor file: 2018_01_01_000000_create_worksuite_new_table.php.
 *
 * KEY PHP-VS-EXPECTATION FLAGS (PHP wins):
 *  - 2024_01_29_052114_lead_changes.php renames leads→deals + lead_contacts→leads etc.,
 *    BUT the whole block is guarded by `Schema::hasTable('lead_contacts')` and NO migration
 *    ever creates `lead_contacts` (grep-confirmed: only mentioned inside that guard). So the
 *    rename DOES NOT RUN. Final tables stay: `leads` (original) + `deals` (created 2023_12_05).
 *    Confirmed against models: Lead→leads, Deal→deals (default table names).
 *  - 2023_12_05_062223_create_lead_contract_table.php: `lead_files` is RENAMED to `deal_files`
 *    (lead_id dropped, deal_id added); `lead_follow_up` + `lead_products` get deal_id added and
 *    lead_id dropped → they are DEAL children now (models DealFollowUp→lead_follow_up,
 *    LeadProduct→lead_products). Also `leads` DROPS agent_id, currency_id, next_follow_up, value.
 *  - `leads.value` re-type ALTER (2023_12_19) is guarded by hasColumn('leads','value') but value
 *    was already dropped → no-op. `leads.client_id` survives (the lead_changes drop is guarded out).
 *  - deal_files rename also appears in 2024_02_02 (same hasTable('lead_files') guard) — runs once.
 *
 * Cross-batch FKs now wired: custom_fields, currencies, products, projects, countries.
 */

/* ============================== LEADS (CRM) ============================== */

/** lead_agents — int PK→bigint id. company_id cascade. +lead_category_id (2024_04_23). */
export const leadAgents = mysqlTable("lead_agents", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  // +2024_04_23_082735_modify_column_in_deals_table.php
  leadCategoryId: int("lead_category_id", { unsigned: true }).references(
    (): AnyMySqlColumn => leadCategory.id,
    { onDelete: "cascade", onUpdate: "cascade" }
  ),
  status: mysqlEnum("status", ["enabled", "disabled"]).default("enabled"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_category — +is_default (2025_01_24). */
export const leadCategory = mysqlTable("lead_category", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  categoryName: varchar("category_name", { length: 255 }).notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  // +2025_01_24_095318
  isDefault: boolean("is_default").default(false).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_sources */
export const leadSources = mysqlTable("lead_sources", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  type: varchar("type", { length: 255 }).notNull().unique(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_status */
export const leadStatus = mysqlTable("lead_status", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  type: varchar("type", { length: 255 }).notNull().unique(),
  priority: int("priority").notNull(),
  default: boolean("default").notNull(),
  labelColor: varchar("label_color", { length: 255 }).default("#ff0000").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_custom_forms — custom_fields_id ALTER (2022_08_22) matches create; FK → custom_fields. */
export const leadCustomForms = mysqlTable("lead_custom_forms", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  customFieldsId: int("custom_fields_id", { unsigned: true }).references(() => customFields.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  fieldDisplayName: varchar("field_display_name", { length: 255 }).notNull(),
  fieldName: varchar("field_name", { length: 255 }).notNull(),
  fieldOrder: int("field_order").notNull(),
  status: mysqlEnum("status", ["active", "inactive"]).default("active"),
  required: boolean("required").default(false),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * leads — int PK. Original leads table; the 2024_01_29 rename to `deals` does NOT run
 * (lead_contacts never created). FINAL columns: agent_id/currency_id/next_follow_up/value
 * DROPPED by 2023_12_05 cleanUpLeadTables(); client_id RETAINED (drop guarded out).
 * Self-deferred FKs: currency_id removed, category_id→lead_category (SET NULL).
 */
export const leads = mysqlTable("leads", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  clientId: int("client_id"), // signed; client_id drop is guarded out (no lead_contacts)
  sourceId: int("source_id"),
  statusId: int("status_id"),
  columnPriority: int("column_priority").notNull(),
  // agent_id DROPPED 2023_12_05
  companyName: varchar("company_name", { length: 255 }),
  website: varchar("website", { length: 255 }),
  address: text("address"),
  salutation: mysqlEnum("salutation", ["mr", "mrs", "miss", "dr", "sir", "madam"]),
  clientName: varchar("client_name", { length: 255 }).notNull(),
  clientEmail: varchar("client_email", { length: 255 }),
  mobile: varchar("mobile", { length: 255 }),
  cell: varchar("cell", { length: 255 }),
  office: varchar("office", { length: 255 }),
  city: varchar("city", { length: 255 }),
  state: varchar("state", { length: 255 }),
  country: varchar("country", { length: 255 }),
  postalCode: varchar("postal_code", { length: 255 }),
  note: text("note"),
  // next_follow_up DROPPED 2023_12_05
  // value DROPPED 2023_12_05
  // currency_id DROPPED 2023_12_05
  categoryId: int("category_id", { unsigned: true }).references(
    (): AnyMySqlColumn => leadCategory.id,
    { onDelete: "set null", onUpdate: "cascade" }
  ),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  hash: text("hash"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_notes — details: text→longText→text(nullable) final (2024_05_06). type/is_lead_show/ask_password booleans. */
export const leadNotes = mysqlTable("lead_notes", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  leadId: int("lead_id", { unsigned: true }).references((): AnyMySqlColumn => leads.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  title: varchar("title", { length: 255 }).notNull(),
  type: boolean("type").default(false),
  memberId: int("member_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  isLeadShow: boolean("is_lead_show").default(false),
  askPassword: boolean("ask_password").default(false),
  details: text("details"), // final: text nullable (2024_05_06_050805)
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_user_notes — pivot users ↔ lead_notes. */
export const leadUserNotes = mysqlTable("lead_user_notes", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  leadNoteId: int("lead_note_id", { unsigned: true })
    .notNull()
    .references((): AnyMySqlColumn => leadNotes.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_products — created 2023_02_04 then deal_id added + lead_id DROPPED (2023_12_05). Model LeadProduct. */
export const leadProducts = mysqlTable("lead_products", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  // lead_id DROPPED 2023_12_05 (createDealsFromLeads)
  productId: int("product_id", { unsigned: true })
    .notNull()
    .references(() => products.id, { onDelete: "cascade", onUpdate: "cascade" }),
  // +deal_id 2023_12_05
  dealId: bigint("deal_id", { mode: "number", unsigned: true }).references(
    (): AnyMySqlColumn => deals.id,
    { onDelete: "cascade", onUpdate: "cascade" }
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_setting — +ticket_round_robin_status (2024_06_05). */
export const leadSetting = mysqlTable("lead_setting", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  status: boolean("status").default(false),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  // +2024_06_05_051512
  ticketRoundRobinStatus: tinyint("ticket_round_robin_status").default(0).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== PIPELINES / DEALS ============================== */

/** lead_pipelines — bigint id ($table->id()). +added_by (2024_05_30, plain signed int nullable, no FK). */
export const leadPipelines = mysqlTable("lead_pipelines", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }),
  slug: varchar("slug", { length: 255 }),
  priority: int("priority").default(0),
  labelColor: varchar("label_color", { length: 255 }).default("#ff0000"),
  default: boolean("default").notNull(),
  // +2024_05_30_062721 — plain integer, nullable, no FK in legacy
  addedBy: int("added_by"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** pipeline_stages — int id. */
export const pipelineStages = mysqlTable("pipeline_stages", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  leadPipelineId: bigint("lead_pipeline_id", { mode: "number", unsigned: true }).references(
    () => leadPipelines.id,
    { onDelete: "cascade", onUpdate: "cascade" }
  ),
  name: varchar("name", { length: 255 }),
  slug: varchar("slug", { length: 255 }),
  priority: int("priority").default(0),
  default: boolean("default").default(false),
  labelColor: varchar("label_color", { length: 255 }).default("#ff0000"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** lead_pipeline_stages — pivot pipeline ↔ stage (lead_pipeline_id is bigint unsigned, pipeline_stages_id int unsigned). */
export const leadPipelineStages = mysqlTable("lead_pipeline_stages", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  leadPipelineId: bigint("lead_pipeline_id", { mode: "number", unsigned: true }).references(
    () => leadPipelines.id,
    { onDelete: "cascade", onUpdate: "cascade" }
  ),
  pipelineStagesId: int("pipeline_stages_id", { unsigned: true }).references(
    () => pipelineStages.id,
    { onDelete: "cascade", onUpdate: "cascade" }
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * deals — bigint id. +category_id (2024_04_23, FK lead_category SET NULL); agent_id FK re-pointed
 * to SET NULL onDelete (2024_04_23). +deal_watcher (2024_04_17, plain signed int nullable).
 * value = double(30,2) default 0; note = longText (column AND a `note()` relation in model).
 * currency_id FK → currencies (SET NULL).
 */
export const deals = mysqlTable("deals", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }),
  columnPriority: int("column_priority").default(0),
  leadPipelineId: bigint("lead_pipeline_id", { mode: "number", unsigned: true }).references(
    () => leadPipelines.id,
    { onDelete: "cascade", onUpdate: "cascade" }
  ),
  pipelineStageId: int("pipeline_stage_id", { unsigned: true }).references(() => pipelineStages.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  leadId: int("lead_id", { unsigned: true }).references(() => leads.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  closeDate: date("close_date"),
  agentId: bigint("agent_id", { mode: "number", unsigned: true }).references(() => leadAgents.id, {
    onDelete: "set null", // re-pointed from CASCADE → SET NULL (2024_04_23)
    onUpdate: "cascade",
  }),
  // +2024_04_23_082735 (after agent_id)
  categoryId: int("category_id", { unsigned: true }).references(() => leadCategory.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  nextFollowUp: mysqlEnum("next_follow_up", ["yes", "no"]).default("yes"),
  value: double("value", { precision: 30, scale: 2 }).default(0),
  note: longtext("note"),
  hash: text("hash"),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  // +2024_04_17_064540 — plain integer nullable
  dealWatcher: int("deal_watcher"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, { onDelete: "cascade" }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** deal_notes — int id. title→varchar(191) nullable (2024_09_05). Created in 2023_12_05 (whenDoesntHave guards re-create). */
export const dealNotes = mysqlTable("deal_notes", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  title: varchar("title", { length: 191 }), // final: nullable (2024_09_05)
  details: longtext("details"),
  dealId: bigint("deal_id", { mode: "number", unsigned: true }).references(() => deals.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, { onDelete: "cascade" }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * deal_files — int id. Renamed from `lead_files` (2023_12_05 / 2024_02_02): lead_id DROPPED,
 * deal_id added. Original lead_files columns retained (filename/hashname/size/google_url/etc).
 */
export const dealFiles = mysqlTable("deal_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  // lead_id DROPPED on rename
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 200 }).notNull(),
  hashname: varchar("hashname", { length: 200 }).notNull(),
  size: varchar("size", { length: 200 }).notNull(),
  description: text("description"),
  googleUrl: varchar("google_url", { length: 255 }),
  dropboxLink: varchar("dropbox_link", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  // +deal_id (rename)
  dealId: bigint("deal_id", { mode: "number", unsigned: true }).references(() => deals.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * lead_follow_up — int id. Model DealFollowUp (deal follow-ups). +status (2023_01_31);
 * lead_id DROPPED + deal_id added (2023_12_05). remind_time/remind_type/send_reminder retained.
 */
export const leadFollowUp = mysqlTable("lead_follow_up", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  // lead_id DROPPED 2023_12_05
  remark: longtext("remark"),
  nextFollowUpDate: datetime("next_follow_up_date"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  eventId: text("event_id"),
  sendReminder: mysqlEnum("send_reminder", ["yes", "no"]).default("no"),
  remindTime: text("remind_time"),
  remindType: mysqlEnum("remind_type", ["minute", "hour", "day"]),
  // +2023_01_31_072924 (after remind_type)
  status: varchar("status", { length: 255 }),
  // +deal_id 2023_12_05
  dealId: bigint("deal_id", { mode: "number", unsigned: true }).references(() => deals.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * deal_histories — bigint id. deal_stage_id RENAMED to deal_stage_from_id + deal_stage_to_id
 * added (FK pipeline_stages SET NULL) by 2024_11_04. The *_id refs below (file/task/follow_up/
 * note/proposal/agent) are plain bigint with NO FK in legacy — kept untyped (no .references()).
 */
export const dealHistories = mysqlTable("deal_histories", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  dealId: bigint("deal_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => deals.id, { onDelete: "cascade" }),
  eventType: varchar("event_type", { length: 255 }).notNull(),
  createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  // renamed from deal_stage_id (2024_11_04) — no FK in legacy
  dealStageFromId: bigint("deal_stage_from_id", { mode: "number", unsigned: true }),
  fileId: bigint("file_id", { mode: "number", unsigned: true }),
  taskId: bigint("task_id", { mode: "number", unsigned: true }),
  followUpId: bigint("follow_up_id", { mode: "number", unsigned: true }),
  noteId: bigint("note_id", { mode: "number", unsigned: true }),
  proposalId: bigint("proposal_id", { mode: "number", unsigned: true }),
  agentId: bigint("agent_id", { mode: "number", unsigned: true }),
  // +2024_11_04_121543 — FK pipeline_stages SET NULL
  dealStageToId: int("deal_stage_to_id", { unsigned: true }).references(() => pipelineStages.id, {
    onDelete: "set null",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== TICKETS / SUPPORT ============================== */

/** ticket_groups */
export const ticketGroups = mysqlTable("ticket_groups", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  groupName: varchar("group_name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_channels */
export const ticketChannels = mysqlTable("ticket_channels", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  channelName: varchar("channel_name", { length: 255 }).notNull().unique(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_types */
export const ticketTypes = mysqlTable("ticket_types", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  type: varchar("type", { length: 255 }).notNull().unique(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_agent_groups */
export const ticketAgentGroups = mysqlTable("ticket_agent_groups", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  agentId: int("agent_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  groupId: int("group_id", { unsigned: true }).references(() => ticketGroups.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  status: mysqlEnum("status", ["enabled", "disabled"]).default("enabled"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_custom_forms — bigint id. custom_fields_id ALTER matches create; FK → custom_fields. */
export const ticketCustomForms = mysqlTable("ticket_custom_forms", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  customFieldsId: int("custom_fields_id", { unsigned: true }).references(() => customFields.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  fieldDisplayName: varchar("field_display_name", { length: 255 }).notNull(),
  fieldName: varchar("field_name", { length: 255 }).notNull(),
  fieldType: varchar("field_type", { length: 255 }).default("text").notNull(),
  fieldOrder: int("field_order").notNull(),
  status: mysqlEnum("status", ["active", "inactive"]).default("active"),
  required: boolean("required").default(false),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_email_settings — bigint id. */
export const ticketEmailSettings = mysqlTable("ticket_email_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  mailUsername: varchar("mail_username", { length: 255 }),
  mailPassword: varchar("mail_password", { length: 255 }),
  mailFromName: varchar("mail_from_name", { length: 255 }),
  mailFromEmail: varchar("mail_from_email", { length: 255 }),
  imapHost: varchar("imap_host", { length: 255 }),
  imapPort: varchar("imap_port", { length: 255 }),
  imapEncryption: varchar("imap_encryption", { length: 255 }),
  status: boolean("status").notNull(),
  verified: boolean("verified").notNull(),
  syncInterval: int("sync_interval").default(1),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * tickets — int id. +ticket_number (2022_10_31, bigint nullable), +project_id (2023_09_12, FK
 * projects onDelete CASCADE, no onUpdate), +group_id (2023_02_15, FK ticket_groups CASCADE).
 * softDeletes. updated_at indexed (2022_08_12). country_id FK → countries (CASCADE/CASCADE).
 */
export const tickets = mysqlTable(
  "tickets",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    // +2022_10_31_130459 (after id)
    ticketNumber: bigint("ticket_number", { mode: "number" }),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    subject: text("subject").notNull(),
    status: mysqlEnum("status", ["open", "pending", "resolved", "closed"]).default("open"),
    priority: mysqlEnum("priority", ["low", "medium", "high", "urgent"]).default("medium"),
    agentId: int("agent_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    channelId: int("channel_id", { unsigned: true }).references(() => ticketChannels.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    typeId: int("type_id", { unsigned: true }).references(() => ticketTypes.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    // +2023_02_15_121548 (after type_id) — FK ticket_groups CASCADE
    groupId: int("group_id", { unsigned: true }).references(() => ticketGroups.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    // +2023_09_12_062223 (after group_id) — FK → projects (onDelete cascade, no onUpdate in legacy)
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
    }),
    closeDate: date("close_date"),
    deletedAt: timestamp("deleted_at"),
    mobile: varchar("mobile", { length: 255 }),
    countryId: int("country_id", { unsigned: true }).references(() => countries.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => ({
    updatedAtIdx: index("tickets_updated_at_index").on(t.updatedAt),
  })
);

/**
 * ticket_replies — int id. softDeletes. +type enum[reply,note] default reply (2024_04_29),
 * +added_by FK users SET NULL (2025_03_11). imap_* retained.
 */
export const ticketReplies = mysqlTable("ticket_replies", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  ticketId: int("ticket_id", { unsigned: true })
    .notNull()
    .references(() => tickets.id, { onDelete: "cascade", onUpdate: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  message: mediumtext("message"),
  // +2024_04_29_123208 (after message)
  type: mysqlEnum("type", ["reply", "note"]).default("reply"),
  deletedAt: timestamp("deleted_at"),
  imapMessageId: varchar("imap_message_id", { length: 255 }),
  imapMessageUid: varchar("imap_message_uid", { length: 255 }),
  imapInReplyTo: varchar("imap_in_reply_to", { length: 255 }),
  // +2025_03_11_052951 — FK users SET NULL
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_files — int id. */
export const ticketFiles = mysqlTable("ticket_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  ticketReplyId: int("ticket_reply_id", { unsigned: true })
    .notNull()
    .references(() => ticketReplies.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  description: text("description"),
  googleUrl: varchar("google_url", { length: 255 }),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  dropboxLink: varchar("dropbox_link", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  externalLinkName: varchar("external_link_name", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_reply_templates — int id. */
export const ticketReplyTemplates = mysqlTable("ticket_reply_templates", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  replyHeading: mediumtext("reply_heading").notNull(),
  replyText: mediumtext("reply_text").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_tag_list — int id. */
export const ticketTagList = mysqlTable("ticket_tag_list", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  tagName: varchar("tag_name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_tags — pivot ticket ↔ tag. */
export const ticketTags = mysqlTable("ticket_tags", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  tagId: int("tag_id", { unsigned: true })
    .notNull()
    .references(() => ticketTagList.id, { onDelete: "cascade", onUpdate: "cascade" }),
  ticketId: int("ticket_id", { unsigned: true })
    .notNull()
    .references(() => tickets.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * ticket_activities — bigint id. NO company_id. status/priority/type are plain strings (not enums)
 * with defaults. channel_id/group_id/type_id/assigned_to all CASCADE in legacy.
 */
export const ticketActivities = mysqlTable("ticket_activities", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  ticketId: int("ticket_id", { unsigned: true })
    .notNull()
    .references(() => tickets.id, { onDelete: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  assignedTo: int("assigned_to", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
  }),
  channelId: int("channel_id", { unsigned: true }).references(() => ticketChannels.id, {
    onDelete: "cascade",
  }),
  groupId: int("group_id", { unsigned: true }).references(() => ticketGroups.id, {
    onDelete: "cascade",
  }),
  typeId: int("type_id", { unsigned: true }).references(() => ticketTypes.id, {
    onDelete: "cascade",
  }),
  status: varchar("status", { length: 255 }).default("open"),
  priority: varchar("priority", { length: 255 }).default("medium"),
  type: varchar("type", { length: 255 }).default("create"),
  content: longtext("content"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** ticket_reply_users — bigint id. pivot ticket_reply ↔ user. */
export const ticketReplyUsers = mysqlTable("ticket_reply_users", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  ticketReplyId: int("ticket_reply_id", { unsigned: true })
    .notNull()
    .references(() => ticketReplies.id, { onDelete: "cascade", onUpdate: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * ticket_settings_for_agents — bigint id. user_id made NULLABLE (2024_07_31). group_id = text
 * (stores serialized group ids). updated_by FK users SET NULL.
 */
export const ticketSettingsForAgents = mysqlTable("ticket_settings_for_agents", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  userId: int("user_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // made nullable 2024_07_31
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  ticketScope: varchar("ticket_scope", { length: 255 }),
  groupId: text("group_id"),
  updatedBy: int("updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ============================== KNOWLEDGE BASE ============================== */

/** knowledge_categories — int id. */
export const knowledgeCategories = mysqlTable("knowledge_categories", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * knowledge_bases — int id. heading already in create (nullable); 2022_08_22 adds it only if
 * missing (no-op here). category_id FK knowledge_categories CASCADE. added_by = plain
 * unsignedInteger NOT NULL with NO FK in legacy.
 */
export const knowledgeBases = mysqlTable("knowledge_bases", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  to: varchar("to", { length: 255 }).default("employee").notNull(),
  heading: varchar("heading", { length: 255 }),
  categoryId: int("category_id", { unsigned: true }).references(() => knowledgeCategories.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  description: mediumtext("description"),
  addedBy: int("added_by", { unsigned: true }).notNull(), // no FK in legacy
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** knowledge_base_files — int id (2022_08_12). added_by/last_updated_by FK users SET NULL. */
export const knowledgeBaseFiles = mysqlTable("knowledge_base_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  knowledgeBaseId: int("knowledge_base_id", { unsigned: true })
    .notNull()
    .references(() => knowledgeBases.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 200 }),
  hashname: varchar("hashname", { length: 200 }),
  size: varchar("size", { length: 200 }),
  externalLinkName: varchar("external_link_name", { length: 255 }),
  externalLink: text("external_link"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
